import {
  AfterContentChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  signal,
  ViewChild,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import {
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { EMPTY, Observable, Subject, throwError } from 'rxjs';
import { SearchResultThumbnailComponent } from '@App/modules/pitch-card-shared/components/search-result-thumbnail/search-result-thumbnail.component';
import { LandingPageService } from '../../../landing-page/landing-page.service';
import {
  CommonModule,
  isPlatformBrowser,
  NgClass,
  NgIf
} from '@angular/common';
import { NoLeadingSpaceDirective } from '@App/common/directives/no-leading-space.directive';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { AppSettings } from '@Shared/app.settings';
import { UniqueComponentId } from 'primeng/utils';
import { AuthActionType } from '../../auth.type';
import {
  BusinessPitch,
  OtpRequestModel,
  UserModel,
  UserReferralModel,
  UserSession
} from '@App/common/models';
import { environment } from '@Environments';
import {
  CommonBindingDataService,
  NotificationService,
  StorageService
} from '@Shared/services';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services';
import { CustomValidators, equalValueValidator } from '@App/shared/utils';
import { CardPackageService } from '@App/modules/cards-packages/services';
import { EmployerPortalService } from '@App/modules/choosen-history/services';
import { PitchCardModalsWrapperService } from '@App/modules/pitch-card-shared/services';
import { UserCommonApiService } from '@App/common/services';
import { VerificationResourceEnum } from '@App/common/enums';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { TranslateModule } from '@ngx-translate/core';

// PitchCardSharedModule removed - using standalone components
import { CountryInputComponent, VerifyFormComponent } from '@Shared/components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoaderService } from '@Shared/components/loader/loader.service';
import { PricingService } from '@App/pages/pricing/pricing.service';

declare const google: any;
declare const AppleID: any;

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    PasswordModule,
    ButtonModule,
    DialogModule,
    ToastModule,
    TranslateModule,
    // PitchCardSharedModule removed
    CountryInputComponent,
    NgClass,
    NgIf,
    NoLeadingSpaceDirective,
    VerifyFormComponent
  ],
  encapsulation: ViewEncapsulation.Emulated
})
export class SignUpComponent
  implements OnInit, OnDestroy, AfterContentChecked, AfterViewInit
{
  @Input() activeLink;
  @Input() isShowVideo = true;
  @Input() isHideTitle = false;
  @Input() isHideHelpCenter = false;
  @Input() isHideMainButton = false;
  @Output() activeTypeChange: EventEmitter<AuthActionType> = new EventEmitter();
  @Output() outputFormGroup: EventEmitter<FormGroup> = new EventEmitter();

  signUpForm: UntypedFormGroup;
  twoStepVerification: UntypedFormGroup;
  smsOtpCode: string = '';
  senderEmail: string;
  submitted = false;
  errorMessage: any;
  businessDetails: BusinessPitch;
  alias = environment.defaultBusinessShareLinkAlias;
  isBrowser: boolean;
  passwordVisibility: { [key: string]: boolean } = {
    password: true,
    repassword: true
  };
  otpTimer = '00';
  timer;
  isTimerRunningSig: WritableSignal<boolean> = signal(false);
  otpRequestModel = new OtpRequestModel();
  resendMessage = false;
  otpModalSig: WritableSignal<boolean> = signal(false);
  isShowPasswordSig: WritableSignal<boolean> = signal(true);
  isVerifiedPhoneSig: WritableSignal<boolean> = signal(false);
  isVerifiedEmailSig: WritableSignal<boolean> = signal(false);
  showEmailVerification = false;
  needVerificationPhoneSig: WritableSignal<boolean> = signal(false);
  needVerificationEmailSig: WritableSignal<boolean> = signal(false);
  scaleFactor = 1;
  showPasswordRequirementsSig: WritableSignal<boolean> = signal(false);
  baseUrl = environment.appBaseUrl;
  defaultResourceType = VerificationResourceEnum.Email;
  countSendingOtp = 0;
  maxCountSendingOtp = 2;
  otpCodeErrorMessage: string;
  payload;
  teamToken: string;
  uniqueId: string;
  queryEmail;
  @ViewChild('pitchCardWrapper', { static: false })
  pitchCardWrapper: ElementRef;
  @ViewChild('businessCard') businessCard: SearchResultThumbnailComponent;
  @ViewChild('videoPlayer', { static: false }) videoPlayer: ElementRef;
  @ViewChild('passwordInput', { static: false })
  passwordInput: ElementRef<HTMLInputElement>;
  @ViewChild('repasswordInput', { static: false })
  repasswordInput: ElementRef<HTMLInputElement>;
  private passwordInputs: { [key: string]: ElementRef<HTMLInputElement> } = {};
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private isLoadingSig: WritableSignal<boolean> = signal(false);

  // inject services
  private formBuilder = inject(UntypedFormBuilder);
  private userCommonService = inject(UserCommonApiService);
  private storageService = inject(StorageService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private commonBindingDataService = inject(CommonBindingDataService);
  private pitchCardModalsWrapperService = inject(PitchCardModalsWrapperService);
  private teamService = inject(EmployerPortalService);
  private cardPackagesService = inject(CardPackageService);
  private welcomePageService = inject(LandingPageService);
  private authService = inject(AuthService);
  private platformId = inject(PLATFORM_ID);
  private loaderService = inject(LoaderService);
  private pricingService = inject(PricingService);

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  get emailIdValue() {
    return this.signUpForm?.value?.emailId;
  }

  // expose signal-backed properties with original names for template compatibility
  get isShowPassword(): boolean {
    return this.isShowPasswordSig();
  }

  get otpModal(): boolean {
    return this.otpModalSig();
  }

  get isTimerRunning(): boolean {
    return this.isTimerRunningSig();
  }

  get showPasswordRequirements(): boolean {
    return this.showPasswordRequirementsSig();
  }

  set showPasswordRequirements(v: boolean) {
    this.showPasswordRequirementsSig.set(!!v);
  }

  get isVerifiedPhone(): boolean {
    return this.isVerifiedPhoneSig();
  }

  get isVerifiedEmail(): boolean {
    return this.isVerifiedEmailSig();
  }

  get needVerificationPhone(): boolean {
    return this.needVerificationPhoneSig();
  }

  get needVerificationEmail(): boolean {
    return this.needVerificationEmailSig();
  }

  get f() {
    return this.twoStepVerification.controls;
  }

  ngOnInit() {
    this.route.queryParams.subscribe((params: any) => {
      this.queryEmail = params['email'];
      this.teamToken = params['teamToken'];
    });

    this.uniqueId = UniqueComponentId();
    this.cardInitSetup();
    this.createForm();

    this.twoStepVerification = this.setFormData();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onResized);
    }
    this.signUpForm.valueChanges.subscribe((value) => {
      this.outputFormGroup.emit(this.signUpForm);
    });
  }

  private initGoogleSignUp(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const waitForGoogle = setInterval(() => {
      if (typeof google !== 'undefined' && google?.accounts?.id) {
        clearInterval(waitForGoogle);
        google.accounts.id.initialize({
          client_id: environment.googleClientId,
          callback: (response: { credential: string }) => {
            this.handleGoogleSignUp(response.credential);
          }
        });
      }
    }, 200);
  }

  private initAppleSignUp(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (!environment.appleClientId || !environment.appleRedirectUri) {
      return;
    }
    const waitForApple = setInterval(() => {
      if (typeof AppleID !== 'undefined' && AppleID?.auth?.init) {
        clearInterval(waitForApple);
        AppleID.auth.init({
          clientId: environment.appleClientId,
          scope: 'name email',
          redirectURI: environment.appleRedirectUri,
          usePopup: true
        });
      }
    }, 200);
  }

  signUpWithGoogle(): void {
    if (
      !isPlatformBrowser(this.platformId) ||
      typeof google === 'undefined' ||
      !google?.accounts?.id
    ) {
      this.notificationService.showNotification(
        'WARNING',
        'Google sign-up is loading, please try again in a moment.'
      );
      return;
    }
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;top:-9999px;left:-9999px;';
    document.body.appendChild(container);
    google.accounts.id.renderButton(container, {
      theme: 'outline',
      size: 'large',
      type: 'standard'
    });
    setTimeout(() => {
      const btn = container.querySelector(
        '[role="button"], button'
      ) as HTMLElement;
      if (btn) {
        btn.click();
      }
      setTimeout(() => container.remove(), 10000);
    }, 100);
  }

  signUpWithApple(): void {
    if (!environment.appleClientId || !environment.appleRedirectUri) {
      this.notificationService.showNotification(
        'WARNING',
        'Apple sign-up is not configured yet.'
      );
      return;
    }
    if (
      !isPlatformBrowser(this.platformId) ||
      typeof AppleID === 'undefined' ||
      !AppleID?.auth?.signIn
    ) {
      this.notificationService.showNotification(
        'WARNING',
        'Apple sign-up is loading, please try again in a moment.'
      );
      return;
    }
    AppleID.auth
      .signIn()
      .then((response: any) => {
        const json = JSON.stringify({
          authorizationCode: response?.authorization?.code,
          givenName: response?.user?.name?.firstName,
          familyName: response?.user?.name?.lastName,
          email: response?.user?.email,
          identityToken: response?.authorization?.id_token
        });
        const idToken = response?.authorization?.id_token;
        if (!idToken) {
          throw new Error('Missing Apple id token');
        }
        this.handleAppleSignUp(json);
      })
      .catch(() => {
        this.notificationService.showNotification(
          'ERROR',
          this.commonBindingDataService.getLabel('err_apple_sign_up_failed')
        );
      });
  }

  handleGoogleSignUp(idToken: string): void {
    this.userCommonService
      .oauthSignIn(idToken, 'Google')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ data }) => {
          this.notificationService.showNotification(
            'SUCCESS',
            this.commonBindingDataService.getLabel(
              'lbl_registration_done_successful'
            )
          );
          const userSession = new UserSession(data);
          this.storageService.setItem(AppSettings.USER_DETAILS, userSession);
          this.storageService.setItemInCookies(
            AppSettings.TOKEN_KEY,
            userSession.token
          );
          this.userCommonService.hideLinks.next(true);
          this.storageService.setItem(AppSettings.SingUpUserData, '');
          this.authService.setAction(null);

          if (this.teamToken) {
            this.goToEditTeamBusiness()
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: ({ data: teamData }) => {
                  if (teamData?.businessId) {
                    this.storageService.setSession(
                      AppSettings.DRAFT_BUSINESS_ID,
                      teamData.businessId
                    );
                    this.cardPackagesService.selectedType =
                      teamData.businessType;
                    this.router.navigate(['create'], {
                      queryParams: { businessId: teamData.businessId }
                    });
                  }
                },
                error: () => {
                  const routeLink = this.getRightLinkAfterSignUp();
                  this.router.navigateByUrl(routeLink);
                }
              });
          } else if (!this.router.url.includes('/pricing')) {
            const routeLink = this.getRightLinkAfterSignUp();
            this.router.navigateByUrl(routeLink);
          }
        },
        error: () => {
          this.notificationService.showNotification(
            'ERROR',
            this.commonBindingDataService.getLabel('err_google_sign_up_failed')
          );
        }
      });
  }

  handleAppleSignUp(idToken: string): void {
    this.isLoadingSig.set(true);
    this.userCommonService
      .oauthSignIn(idToken, 'Apple')
      .pipe(
        takeUntil(this.destroy$),
        switchMap(({ data }) => {
          this.userCommonService.clearUserDataForSwitch();
          const userSession = new UserSession(data);
          this.storageService.setItem(AppSettings.USER_DETAILS, userSession);
          this.storageService.setItemInCookies(
            AppSettings.TOKEN_KEY,
            userSession.token
          );
          this.userCommonService.hideLinks.next(true);
          this.storageService.setItem(AppSettings.SingUpUserData, '');
          
          const context = this.authService.getCurrentContext();
          if (context?.isFromPricing && context.returnToStep) {
            this.pricingService.goBackAfterAuthClose(context.returnToStep);
          }

          window.scrollTo(0, 0);
          this.isLoadingSig.set(false);
          this.loaderService.hide();

          const inviteToken = sessionStorage.getItem('inviteToken');
          if (inviteToken) {
            return this.userCommonService.acceptInvite(inviteToken);
          }

          this.authService.setAction(null);
          this.checkTeamToken();
          return EMPTY;
        })
      )
      .subscribe({
        next: () => {
          setTimeout(() =>
            this.notificationService.showNotification(
              'SUCCESS',
              'Your invite has been accepted'
            )
          );
          sessionStorage.removeItem('inviteToken');
          this.authService.setAction(null);
          this.checkTeamToken();
        },
        error: () => {
          this.isLoadingSig.set(false);
          this.loaderService.hide();
          this.notificationService.showNotification(
            'ERROR',
            this.commonBindingDataService.getLabel('err_apple_sign_up_failed')
          );
        }
      });
  }

  private checkTeamToken(): void {
    if (this.teamToken) {
      this.goToEditTeamBusiness()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: ({ data }) => {
            if (data?.businessId) {
              this.storageService.setSession(
                AppSettings.DRAFT_BUSINESS_ID,
                data.businessId
              );
              this.cardPackagesService.selectedType = data.businessType;
              this.router.navigate(['create'], {
                queryParams: { businessId: data.businessId }
              });
            }
          },
          error: () => {
            const routeLink = this.getRightLinkAfterSignUp();
            this.router.navigateByUrl(routeLink);
          }
        });
    } else if (!this.router.url.includes('/pricing')) {
      const routeLink = this.getRightLinkAfterSignUp();
      this.router.navigateByUrl(routeLink);
    }
  }

  createForm() {
    this.signUpForm = this.formBuilder.group(
      {
        firstName: [
          '',
          [Validators.required, Validators.pattern(AppSettings.NAME_PATTERN)]
        ],
        lastName: [
          '',
          [Validators.required, Validators.pattern(AppSettings.NAME_PATTERN)]
        ],
        phone: [null, [CustomValidators.phonePattternValidator()]],
        emailId: [
          '',
          [Validators.required, Validators.pattern(AppSettings.EMAIL_PATTERN)]
        ],
        zipCode: [
          '',
          Validators.pattern(
            '(^\\d{5}(-\\d{4})?$)|(^[A-Za-z]\\d[A-Za-z]\\s?\\d[A-Za-z]\\d$)'
          )
        ],
        password: [
          '',
          [
            Validators.required,
            CustomValidators.patternValidator(/\d/, {
              hasNumber: true
            }),
            CustomValidators.patternValidator(/[A-Z]/, {
              hasCapitalCase: true
            }),
            CustomValidators.patternValidator(/[a-z]/, {
              hasSmallCase: true
            }),
            Validators.minLength(8)
          ]
        ],
        repassword: ['', Validators.required],
        refemailId: this.queryEmail
          ? [this.queryEmail, Validators.pattern(AppSettings.EMAIL_PATTERN)]
          : ['', Validators.pattern(AppSettings.EMAIL_PATTERN)]
      },
      { validator: equalValueValidator('password', 'repassword') }
    );
  }

  getPayload() {
    this.payload = new UserModel();
    this.payload.firstName = this.signUpForm.value.firstName;
    this.payload.lastName = this.signUpForm.value.lastName;
    this.payload.phone = this.signUpForm.value.phone;
    this.payload.emailId = this.signUpForm.value.emailId;
    this.payload.password = this.signUpForm.value.password;
    this.payload.zipCode = this.signUpForm.value.zipCode;
    this.payload.userReferralModel = new UserReferralModel();
    this.payload.userReferralModel.referralEmail =
      this.signUpForm.value.emailId;
    if (this.signUpForm.value.refemailId) {
      this.payload.userReferralModel.senderEmail =
        this.signUpForm.value.refemailId;
      this.senderEmail = this.signUpForm.value.refemailId;
    } else {
      this.senderEmail = '';
    }
    return this.payload;
  }

  goToEditTeamBusiness(): Observable<any> {
    return this.teamService.joinTeamByInvite(this.teamToken);
  }

  setFormData() {
    return this.formBuilder.group(
      {
        otpCode: [
          '',
          [
            Validators.pattern(AppSettings.DIGITCODE_PATTERN),
            Validators.minLength(4)
          ]
        ]
      },
      { validators: this.otpValidator }
    );
  }

  otpValidator(formGroup): any {
    return formGroup.controls['otpCode'].valid;
  }

  cardInitSetup() {
    this.userCommonService
      .getBusinessPitchModelByAlias(this.alias)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyCardData) => {
        this.businessDetails = companyCardData?.data;
      });
  }

  doSignup(): Observable<any> {
    this.submitted = true;
    if (this.signUpForm.invalid) {
      return;
    } else if (
      this.signUpForm.get('repassword').value !==
      this.signUpForm.get('password').value
    ) {
      this.signUpForm.get('repassword').setErrors({
        notEqual: true
      });
      return;
    } else {
      const payload = this.getPayload();
      payload.otpCode = this.twoStepVerification.get('otpCode').value;
      return this.userCommonService.signUp(payload);
    }
  }

  getRightLinkAfterSignUp() {
    if (this.route.snapshot.queryParams.serviceUrl) {
      return this.route.snapshot.queryParams.serviceUrl;
    } else {
      return 'pricing';
    }
  }

  togglePasswordVisibility(fieldName: 'password' | 'repassword'): void {
    if (!this.isBrowser) {
      return;
    }

    const inputRef = this.passwordInputs[fieldName];
    const inputElement =
      inputRef?.nativeElement ||
      (document.getElementById(fieldName) as HTMLInputElement);

    if (inputElement) {
      const isCurrentlyHidden = inputElement.type === 'password';
      inputElement.type = isCurrentlyHidden ? 'text' : 'password';
      this.passwordVisibility[fieldName] = !isCurrentlyHidden;
    }
  }

  isPasswordVisible(fieldName: 'password' | 'repassword'): boolean {
    return this.passwordVisibility[fieldName] ?? true;
  }

  togglePassword(): void {
    this.togglePasswordVisibility('password');
  }

  toggleRepassword(): void {
    this.togglePasswordVisibility('repassword');
  }

  onResized() {
    const initialCardWidth = 370;
    const clientWidth = this.pitchCardWrapper?.nativeElement?.parentElement;
    if (this.pitchCardWrapper) {
      if (clientWidth - 60 < initialCardWidth) {
        this.scaleFactor = (clientWidth - 60) / initialCardWidth;
      } else {
        this.scaleFactor = 1;
      }
    }
  }

  ngAfterViewInit(): void {
    this.initGoogleSignUp();
    this.initAppleSignUp();
  }

  ngAfterContentChecked() {
    this.onResized();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.resetForm();
  }

  resetForm(): void {
    this.signUpForm?.reset();
    this.signUpForm?.markAsPristine();
    this.signUpForm?.markAsUntouched();
    this.twoStepVerification?.reset();
    this.twoStepVerification?.markAsPristine();
    this.twoStepVerification?.markAsUntouched();
    this.submitted = false;
    this.errorMessage = null;
    this.resetModal();
  }

  fillOtpRequestModel(type) {
    this.otpRequestModel.emailId = this.signUpForm.value.emailId;
    this.otpRequestModel.phone = this.signUpForm.value.phone;
    this.otpRequestModel.newUser = AppSettings.NEW_USER;
    this.otpRequestModel.firstName = this.signUpForm.value.firstName;
    this.otpRequestModel.lastName = this.signUpForm.value.lastName;
    this.otpRequestModel.type = type;
  }

  sendOtp(event, e) {
    if (this.isNotAbleSendCode()) {
      return;
    }
    this.fillOtpRequestModel(e.type);
    this.userCommonService
      .sendOtpForSignup(this.otpRequestModel)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ message }) => {
          this.otpTimer = '59';
          this.otpModalSig.set(true);
          this.isTimerRunningSig.set(true);
          this.countSendingOtp += 1;
          this.defaultResourceType = this.setVerificationTypeResource();
          this.startTimer(e.type);
          this.notificationService.showNotification('SUCCESS', message);
          e.type == VerificationResourceEnum.Email
            ? this.needVerificationEmailSig.set(true)
            : this.needVerificationPhoneSig.set(true);
        },
        error: ({ code }) => {
          switch (code) {
            case 1017:
              this.signUpForm.get('phone').setErrors({ notUnique: true });
              break;
            case 1013:
              this.signUpForm.get('emailId').setErrors({ notUnique: true });
              break;
            default:
              console.log('err');
              break;
          }
        }
      });
  }

  setVerificationTypeResource(): VerificationResourceEnum {
    if (this.countSendingOtp < this.maxCountSendingOtp) {
      return this.defaultResourceType;
    }
    if (this.countSendingOtp === this.maxCountSendingOtp) {
      this.countSendingOtp = 0;
      return this.defaultResourceType === VerificationResourceEnum.Phone
        ? VerificationResourceEnum.Email
        : VerificationResourceEnum.Phone;
    }
  }

  isNotAbleSendCode() {
    return !(
      this.signUpForm.get('phone').valid &&
      this.signUpForm.get('emailId').valid &&
      this.signUpForm.get('firstName').valid &&
      this.signUpForm.get('lastName').valid
    );
  }

  verifyOtp(e) {
    this.addFormSubscriptions();

    this.twoStepVerification
      .get('otpCode')
      .patchValue(e && e.otpCode ? e.otpCode : '');
    if (this.twoStepVerification.get('otpCode').valid) {
      this.otpCodeErrorMessage = '';
      this.otpRequestModel.smsOtpCode = this.twoStepVerification.value.otpCode;

      this.userCommonService
        .verifyEmailSmsOTP(this.otpRequestModel)
        .pipe(
          takeUntil(this.destroy$),
          switchMap((verifyResult) => {
            this.otpCodeErrorMessage = '';
            this.notificationService.showNotification(
              'SUCCESS',
              verifyResult.message
            );
            this.isVerifiedEmailSig.set(true);
            return this.doSignup().pipe(
              catchError((error) => throwError({ signUpError: error }))
            );
          }),
          switchMap((signUpRes) => {
            this.resetModal();
            if (signUpRes) {
              this.notificationService.showNotification(
                'SUCCESS',
                this.commonBindingDataService.getLabel(
                  'lbl_registration_done_successful'
                )
              );
              const userSession = new UserSession(signUpRes.data);
              userSession.email = this.signUpForm.value.emailId;
              this.storageService.setItem(
                AppSettings.USER_DETAILS,
                userSession
              );
              this.storageService.setItemInCookies(
                AppSettings.TOKEN_KEY,
                userSession.token
              );
              this.userCommonService.hideLinks.next(true);
              this.storageService.setItem(AppSettings.SingUpUserData, '');
              this.authService.setAction(null);
              // track sign up completion
              if (this.teamToken) {
                return this.goToEditTeamBusiness().pipe(
                  catchError((error) =>
                    throwError({ joinByInviteError: error })
                  )
                );
              } else if (!this.router.url.includes('/pricing')) {
                const routeLink = this.getRightLinkAfterSignUp();
                const link = `${routeLink}`;
                this.router.navigateByUrl(link);
              }
            }
          }),
          catchError((err) => throwError({ mainError: err }))
        )
        .subscribe({
          next: ({ data, signUpError }) => {
            if (data) {
              this.welcomePageService.smoothVerticalScrolling(0, 300);
              if (data?.businessId) {
                this.storageService.setSession(
                  AppSettings.DRAFT_BUSINESS_ID,
                  data.businessId
                );
                this.cardPackagesService.selectedType = data.businessType;
                this.router.navigate(['create'], {
                  queryParams: { businessId: data.businessId }
                });
              }
              if (signUpError) {
                switch (signUpError.Code) {
                  case 1017:
                    this.signUpForm.get('phone').setErrors({ notUnique: true });
                    break;
                  case 1013:
                    this.signUpForm
                      .get('emailId')
                      .setErrors({ notUnique: true });
                    break;
                  default:
                    this.errorMessage = signUpError.Message;
                    break;
                }
              }
            }
          },
          error: (error) => {
            const { signUpError, joinByInviteError } = error.mainError;
            if (error?.mainError && !signUpError && !joinByInviteError) {
              this.twoStepVerification
                .get('otpCode')
                .setErrors({ pattern: true });
              this.otpCodeErrorMessage = error.verifyOtpError?.message
                ? error.verifyOtpError.message
                : this.commonBindingDataService.getLabel('label_incorrect_otp');
            }
            if (signUpError?.Code) {
              switch (signUpError.Code) {
                case 1017:
                  this.signUpForm.get('phone').setErrors({ notUnique: true });
                  break;
                case 1013:
                  this.signUpForm.get('emailId').setErrors({ notUnique: true });
                  break;
                default:
                  this.notificationService.showNotification(
                    'WARNING',
                    signUpError?.Message
                      ? signUpError.Message
                      : this.commonBindingDataService.getLabel('err_server')
                  );
                  break;
              }
            }
            if (joinByInviteError) {
              this.notificationService.showNotification(
                'WARNING',
                error.joinByInviteError.message
              );
            }
          }
        });
    }
  }

  addFormSubscriptions() {
    this.handleContactFieldChanges('phone')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isVerifiedPhoneSig.set(false);
        },
        error: (error) =>
          this.notificationService.showNotification('WARNING', error.message)
      });
    this.handleContactFieldChanges('emailId')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isVerifiedEmailSig.set(false);
        },
        error: (error) =>
          this.notificationService.showNotification('WARNING', error.message)
      });
  }

  handleContactFieldChanges(field): Observable<any> {
    return this.signUpForm.get(field).valueChanges;
  }

  goToSignIn() {
    this.activeTypeChange.emit('SIGN_IN');
  }

  startTimer(type) {
    let timerValue = 59;
    this.timer = setInterval(() => {
      if (timerValue) {
        this.otpTimer = timerValue < 10 ? '0' + timerValue : '' + timerValue;
        --timerValue;
      } else {
        this.otpTimer = '00';
        this.isTimerRunningSig.set(false);
        this.resendMessage = true;
        if (
          type === 'phone' &&
          !(this.isVerifiedEmail || this.isVerifiedPhone)
        ) {
          this.showEmailVerification = true;
          this.notificationService.showNotification(
            'WARNING',
            'If you didn’t receive the code via text, please click Email Code button to receive it by email.'
          );
        }
        clearInterval(this.timer);
      }
    }, 1000);
  }

  focusPhone() {
    (<HTMLElement>document.getElementById('contactNumber').children[0]).focus();
  }

  handlePitchCardTitleClick(event: any) {
    const { title, businessDetails } = event;

    this.pitchCardModalsWrapperService.setUniqueId(this.uniqueId);
    this.pitchCardModalsWrapperService.setCurrentBusiness(businessDetails);
    this.pitchCardModalsWrapperService.onTitleClick(title);
  }

  resetModal() {
    this.otpTimer = '00';
    clearInterval(this.timer);
    this.countSendingOtp = 0;
    this.defaultResourceType = VerificationResourceEnum.Phone;
    this.otpCodeErrorMessage = '';
    this.twoStepVerification.get('otpCode').patchValue('');
    this.otpModalSig.set(false);
  }

  blockChars(e) {
    e.target.value = e.target.value
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1');
  }

  async onPaste(e: KeyboardEvent, control) {
    if ((e.ctrlKey || e.metaKey) && e.keyCode == 86) {
      if (isPlatformBrowser(this.platformId)) {
        await navigator.clipboard
          .readText()
          .then((pastedData) => {
            const inputData = pastedData;
            if (control === 'zipCode') {
              this.toUppercase(inputData);
            }
            this.signUpForm
              .get(control)
              .patchValue(
                inputData.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1')
              );
          })
          .catch((err) => {
            this.notificationService.showNotification(
              'ERROR',
              'Something going wrong'
            );
          });
      }
    }
  }

  toUppercase(type: string) {
    switch (type) {
      case 'zipCode':
        this.signUpForm
          .get('zipCode')
          .patchValue(this.signUpForm.get('zipCode').value.toUpperCase());
    }
  }

  protected visibleChange(visible: boolean) {
    this.otpModalSig.set(visible);
    if (!visible) {
      this.resetModal();
    }
  }
}
